import React, { use } from "react";
import { useAtom } from "jotai";
import ScreenCard from "../ScreenCard";
import { newMissionState } from "../../atoms/core";
import {
    CreateMissionMode,
    CreateMissionScreensContext,
} from "../../contexts/CreateMissionManager";
import { WorkerInfo } from "../../__generated__/graphql";
import MissionWorker from "../../components/editors/MissionWorker";
import { useI18n } from "../../hooks/i18n-hooks";
import { Bubble } from "../../components/Bubble";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";
import { useSetAlert } from "../../hooks/use-set-alert";

const CreateMissionWorker = () => {
    const i18n = useI18n();
    const [mission, setMission] = useAtom(newMissionState);
    const { validateScreen, createMissionMode } = use(CreateMissionScreensContext);
    const { setAlert } = useSetAlert();
    const setWorkerinfo = React.useCallback(
        (value: WorkerInfo) => {
            if (createMissionMode === CreateMissionMode.New) {
                setMission({
                    ...mission,
                    workerInfo: value,
                });
            } else {
                setAlert({
                    title: i18n.t(
                        "screens.user.create_mission_worker.cannot_change_employee.title",
                    ),
                    message: i18n.t(
                        "screens.user.create_mission_worker.cannot_change_employee.description",
                    ),
                });
            }
        },
        [createMissionMode, i18n, mission, setAlert, setMission],
    );
    const validate = React.useCallback(() => {
        void validateScreen("CreateMissionWorker");
    }, [validateScreen]);

    const origin = useBubbleStepOrigin("CreateMissionWorker");

    // this is a small optimization to avoid re-rendering the whole screen when mission didn't change
    // this happens when the user goes back and forth between screens
    const content = React.useMemo(() => {
        return (
            <ScreenCard
                buttons={[
                    {
                        title: i18n.translate("next"),
                        onPress: validate,
                        isValid: !!mission.workerInfo,
                    },
                ]}
                withAssistanceButton
            >
                <Bubble
                    title={i18n.t("screens.user.create_mission_worker.title")}
                    bubbleStyle={{ paddingBottom: 0 }}
                    origin={origin}
                >
                    <MissionWorker mission={mission} setWorkerInfo={setWorkerinfo}></MissionWorker>
                </Bubble>
            </ScreenCard>
        );
    }, [i18n, validate, mission, origin, setWorkerinfo]);

    return content;
};

export default CreateMissionWorker;
